import React, { useEffect, useState } from 'react'
import { Tree, Classes, Icon, Colors } from '@blueprintjs/core'
import { useTreeFunctions } from '../../hooks/useTreeFunctions'
import { cents } from '../../util/formatters'
import { generateMatchType } from './KeywordsTree'
import { Link } from 'react-router-dom'
import { Tooltip } from '../ui/tooltip'
import { parseExpression } from '../../util/parse_expression'

export const SmartBidsTree = ({ title, data, negative = false, type = 'keywords' }) => {
    const [treeItems, setTreeItems] = useState({})
    const { handleNodeClick, handleNodeCollapse, handleNodeExpand, nodes, setNodes } =
        useTreeFunctions()

    const generateChildLabel = (item) => {
        const direction = item.newBid < item.oldBid ? 'decrease' : 'increase'
        const verbatim = item.targetAcos < item.currentAcos ? 'decrease' : 'increase'
        const currentAcosRounded = Math.round(item.currentAcos * 100) / 100
        const targetAcosRounded = Math.round(item.targetAcos * 100) / 100
        const matchType = generateMatchType(item.matchType)
        const label = (
            <>
                Adjusting bid for "
                {item.expression ? parseExpression(item.expression) : item?.keywordText}" from
                {item.oldBid} to {item.newBid}. Trying to {verbatim} ACoS from {currentAcosRounded}%
                to target of
                {targetAcosRounded}%.
            </>
        )
        let warning = null
        if (item.minBid && item.newBid === item.minBid) {
            warning = `Couldn't decrease bid below your min of ${item.minBid}`
        } else if (item.maxBid && item.newBid === item.maxBid) {
            warning = `Couldn't increase bid above your max of ${item.maxBid}`
        }

        const color = direction == 'increase' ? 'green' : 'red'

        return (
            <div className="flex items-center">
                <Icon
                    icon={
                        direction == 'increase' ? (
                            <Icon
                                icon="circle-arrow-up"
                                className="pr-2 pt-1"
                                style={{ float: 'left' }}
                                color={color}
                            />
                        ) : (
                            <Icon
                                icon="circle-arrow-down"
                                className="pr-2 pt-1"
                                style={{ float: 'left' }}
                                color={color}
                            />
                        )
                    }
                />
                {type == 'keywords' ? (
                    <span style={{ float: 'left' }}>{matchType} -&nbsp;</span>
                ) : (
                    ''
                )}
                {warning && (
                    <Tooltip wrapperTagName="div" content={<span>{warning}</span>}>
                        <div className="flex items-center">
                            <Icon
                                icon={
                                    <Icon
                                        icon="warning-sign"
                                        className="mr-2"
                                        color={Colors.ORANGE3}
                                    />
                                }
                            />
                        </div>
                    </Tooltip>
                )}
                <span className="mb-1">{label}</span>
            </div>
        )
    }

    const generateParentLabel = (items) => {
        const result = (
            <p className={'m-0'}>
                {items.length == data.length ? 'All' : items.length} for Campaign{' '}
                <Link to={'/campaigns/' + items[0].campaignId}>{items[0].campaignName}</Link>
                {!negative && ', '}
                {!negative ? (
                    <Link to={'/ad-groups/' + items[0].adGroupId}>{items[0].adGroupName}</Link>
                ) : (
                    ''
                )}
            </p>
        )

        return result
    }

    const load = (_) => {
        if (data.length == 0) return

        const output = {}
        if (type == 'keywords') {
            data.forEach((item) => {
                const formattedData = {
                    currentAcos: item.current_acos,
                    targetAcos: item.target_acos,
                    matchType: item.keyword?.matchType,
                    keywordText: item.keyword?.keywordText,
                    newBid: cents(item.new_bid),
                    oldBid: cents(item.old_bid),
                    minBid: item.min_bid ? cents(item.min_bid) : null,
                    maxBid: cents(item.max_bid),
                    adGroupName: item.adGroupData?.name,
                    adGroupId: item.adGroupData?.ad_group_id,
                    campaignName: item.campaignData.name,
                    campaignId: item.campaignData.campaign_id,
                }

                if (output.hasOwnProperty(item.keyword?.campaignId)) {
                    output[item.keyword?.campaignId].push(formattedData)
                } else {
                    output[item.keyword?.campaignId] = [formattedData]
                }
            })
        } else if (type == 'targets') {
            data.forEach((item) => {
                const formattedData = {
                    currentAcos: item.current_acos,
                    targetAcos: item.target_acos,
                    expression: item.targetData?.expression,
                    keywordText: item.target?.targetId,
                    newBid: cents(item.new_bid),
                    oldBid: cents(item.old_bid),
                    adGroupName: item.adGroupData?.name,
                    adGroupId: item.adGroupData?.ad_group_id,
                    campaignName: item.campaignData.name,
                    campaignId: item.campaignData.campaign_id,
                }

                if (output.hasOwnProperty(item.target?.campaignId)) {
                    output[item.target?.campaignId].push(formattedData)
                } else {
                    output[item.target?.campaignId] = [formattedData]
                }
            })
        }

        setTreeItems(output)
    }

    const renderTree = (_) => {
        const output = []

        Object.keys(treeItems).forEach((key) => {
            const items = treeItems[key]
            const currentKey = output.push({
                id: key,
                hasCaret: true,
                label: generateParentLabel(items),
                isExpanded: true,
                childNodes: [],
            })

            items.forEach((item, i) => {
                output[currentKey - 1].childNodes.push({
                    id: currentKey - 1 + '-' + i,
                    hasCaret: false,
                    isExpanded: true,
                    label: <span>{generateChildLabel(item)}</span>,
                })
            })
        })

        setNodes(output)
    }

    useEffect(() => {
        load()
    }, [])

    useEffect(() => {
        renderTree()
    }, [treeItems])

    return (
        <div className={Classes.ELEVATION_0 + ' p-4 dry-run rounded shadow'}>
            <h2 className="text-xl font-bold pb-2">
                Changing bids to {data.length} {title}
            </h2>
            <Tree
                onNodeClick={handleNodeClick}
                onNodeCollapse={handleNodeCollapse}
                onNodeExpand={handleNodeExpand}
                contents={nodes}
            />
        </div>
    )
}
